import type { api } from "./api";

type Seat = Awaited<ReturnType<typeof api.sessionSeating>>["seats"][number];

export interface Aggregate {
  key: string;
  label: string;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  seatCount: number;
  occupiedCount: number;
  dominantFill: string;
  /** Raw seats in this group — used to compute an on-demand hover breakdown (see lib/breakdown.ts). Kept out of the hot render path; only read when a tooltip is shown. */
  seats: Seat[];
}

function aggregate(seats: Seat[], keyOf: (seat: Seat) => string, labelOf: (seat: Seat) => string, fillOf: (seat: Seat) => string): Aggregate[] {
  const groups = new Map<string, { seats: Seat[]; fillCounts: Map<string, number> }>();

  for (const seat of seats) {
    const key = keyOf(seat);
    if (!groups.has(key)) groups.set(key, { seats: [], fillCounts: new Map() });
    const group = groups.get(key)!;
    group.seats.push(seat);
    const fill = fillOf(seat);
    group.fillCounts.set(fill, (group.fillCounts.get(fill) ?? 0) + 1);
  }

  const result: Aggregate[] = [];
  for (const [key, group] of groups) {
    const xs = group.seats.map((s) => s.positionX);
    const ys = group.seats.map((s) => s.positionY);
    let dominantFill = "#d8dbe2";
    let dominantCount = -1;
    for (const [fill, count] of group.fillCounts) {
      if (count > dominantCount) {
        dominantCount = count;
        dominantFill = fill;
      }
    }
    result.push({
      key,
      label: labelOf(group.seats[0]),
      minX: Math.min(...xs),
      maxX: Math.max(...xs),
      minY: Math.min(...ys),
      maxY: Math.max(...ys),
      seatCount: group.seats.length,
      occupiedCount: group.seats.filter((s) => s.occupant).length,
      dominantFill,
      seats: group.seats,
    });
  }
  return result;
}

/** One aggregate per block — used for the low-zoom "overview" layer (block label + occupancy). */
export function aggregateByBlock(seats: Seat[], fillOf: (seat: Seat) => string): Aggregate[] {
  return aggregate(
    seats,
    (s) => s.block,
    (s) => s.block,
    fillOf,
  );
}

/** One aggregate per (block, row) — used for the mid-zoom "row" layer. Label includes the block so identical row numbers across neighboring blocks stay distinguishable. */
export function aggregateByRow(seats: Seat[], fillOf: (seat: Seat) => string): Aggregate[] {
  return aggregate(
    seats,
    (s) => `${s.block}-${s.row}`,
    (s) => `${s.block}-${s.row}`,
    fillOf,
  );
}
